import React from "react";

import { Text, View } from "@react-pdf/renderer";
import { TFunction } from "i18next";

import { EntityPhoto } from "@/components/alerts/entities/entityPhotos";
import { EntityRow } from "@/components/alerts/entities/entityRows";

import { dlPDFEntityStyles as styles } from "./ReactPDFStyling";

/**
 * The bulletin's people, vehicles and items, laid out the way `BulletinEntityList` shows them on
 * the detail view: who or what it is on the left, the stored values and the officer's description
 * on the right. It takes the place of the tag grid the PDF used to print here.
 *
 * Each row is unbreakable so an entity's heading never ends a page with its description on the
 * next — the description is the part recipients need, and it is useless without the label.
 */

const Field: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <View style={styles.field}>
    <Text style={styles.field_label}>{label}</Text>
    <Text style={styles.field_value}>{value}</Text>
  </View>
);

interface PDFEntityListProps {
  rows: EntityRow[];
  /** Keyed by entity id: the gallery letters of the photos each entity appears in. */
  photos: Record<string, EntityPhoto[]>;
  /** Bound to the `bulletin/entities` namespace. */
  t: TFunction;
}

const PDFEntityList: React.FC<PDFEntityListProps> = ({ rows, photos, t }) => (
  <View style={styles.container}>
    {rows.map((row, index) => {
      const shots = photos[row.id] ?? [];

      return (
        <View
          key={row.id}
          style={
            index === rows.length - 1
              ? styles.row
              : { ...styles.row, ...styles.row_divider }
          }
          wrap={false}
        >
          <View style={styles.identity}>
            <Text style={styles.heading}>{row.heading}</Text>
            <Field label={row.kind} value={row.name} />
            {row.dateOfBirth !== "" && (
              <Field label={t("dob")} value={row.dateOfBirth} />
            )}
            {shots.length > 0 && (
              <Field
                label={t(shots.length > 1 ? "photos" : "photo")}
                value={shots.map((shot) => shot.letter).join(", ")}
              />
            )}
          </View>

          <View style={styles.details}>
            {row.fields.length > 0 && (
              <View style={styles.stored_fields}>
                {row.fields.map((field) => (
                  <View key={field.label} style={styles.stored_field}>
                    <Field label={field.label} value={field.value} />
                  </View>
                ))}
              </View>
            )}
            {row.description !== "" && (
              <Text style={styles.description}>{row.description}</Text>
            )}
          </View>
        </View>
      );
    })}
  </View>
);

export default PDFEntityList;
